import type React from "react";
import Seo from "../components/Seo";

interface OfferCardProps {
  title: string;
  price: string;
  description: string;
  link: string;
  variant?: "primary" | "accent";
}

const BADGE_COPY: Record<"primary" | "accent", string> = {
  primary: "Best Value",
  accent: "Great Value",
};

const OfferCard: React.FC<OfferCardProps> = ({
  title,
  price,
  description,
  link,
  variant,
}) => {
  const accentColor =
    variant === "primary"
      ? "var(--color-primary)"
      : variant === "accent"
        ? "var(--color-brand)"
        : undefined;

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={`relative block p-6 rounded-lg transition-transform hover:scale-105 ${
        variant ? "shadow-xl md:-my-2" : "shadow-md"
      }`}
      style={{
        backgroundColor:
          variant === "primary" ? "var(--color-primary)" : "white",
        border: `3px solid ${accentColor ?? "var(--color-primary)"}`,
      }}
    >
      {variant && (
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold tracking-wide uppercase whitespace-nowrap"
          style={{
            backgroundColor:
              variant === "primary"
                ? "var(--color-complementary)"
                : accentColor,
            color: variant === "primary" ? "var(--color-primary)" : "white",
          }}
        >
          {BADGE_COPY[variant]}
        </span>
      )}
      <p
        className="text-2xl font-bold mb-3"
        style={{
          color:
            variant === "primary"
              ? "white"
              : (accentColor ?? "var(--color-primary)"),
        }}
      >
        {title}
      </p>
      <p
        className="text-4xl font-bold mb-3"
        style={{ color: variant === "primary" ? "white" : "var(--color-text)" }}
      >
        {price}
      </p>
      <p
        className="text-base"
        style={{
          color: variant === "primary" ? "white" : "var(--color-text)",
          opacity: variant === "primary" ? 0.9 : 1,
        }}
      >
        {description}
      </p>
    </a>
  );
};

const INTRO_OFFERS: OfferCardProps[] = [
  {
    title: "First Class Free",
    price: "$0",
    description: "Perfect for first-time riding or gifting",
    link: "https://clients.mindbodyonline.com/classic/ws?studioid=5747548&stype=43&prodid=100082",
  },
  {
    title: "Monthly Unlimited Intro Offer",
    price: "$99",
    description: "Unlimited classes for 30 days",
    link: "https://clients.mindbodyonline.com/classic/ws?studioid=5747548&stype=43&prodid=100057",
  },
];

const CLASS_PACKS: OfferCardProps[] = [
  {
    title: "5 Pack",
    price: "$100",
    description: "Classes expire 6 months from purchase",
    link: "https://clients.mindbodyonline.com/classic/ws?studioid=5747548&stype=43&prodid=100003",
  },
  {
    title: "10 Pack",
    price: "$199",
    description: "Classes expire 12 months from purchase",
    link: "https://clients.mindbodyonline.com/classic/ws?studioid=5747548&stype=43&prodid=100004",
    variant: "accent",
  },
];

const PROMOTIONS: OfferCardProps[] = [
  {
    title: "Summer Bonus Pack",
    price: "$199",
    description: "10 classes + 2 bonus classes = 12 classes total",
    link: "https://clients.mindbodyonline.com/classic/ws?studioid=5747548&stype=43&prodid=100096",
    variant: "accent",
  },
];

const DROP_IN: OfferCardProps[] = [
  {
    title: "Drop In",
    price: "$23",
    description: "Single class expires 6 months from purchase",
    link: "https://clients.mindbodyonline.com/classic/ws?studioid=5747548&stype=43&prodid=100002",
  },
];

const AUTO_RENEW: OfferCardProps[] = [
  {
    title: "4 Class Recurring",
    price: "$75",
    description: "Billed monthly, 4 classes per month",
    link: "https://clients.mindbodyonline.com/classic/ws?studioid=5747548&stype=40&prodid=101",
  },
  {
    title: "Unlimited Recurring",
    price: "$185",
    description:
      "Unlimited classes + 1 guest pass/month + booking 10 days in advance",
    link: "https://clients.mindbodyonline.com/classic/ws?studioid=5747548&stype=40&prodid=100",
    variant: "primary",
  },
  {
    title: "8 Class Recurring",
    price: "$140",
    description: "Billed monthly, 8 classes per month",
    link: "https://clients.mindbodyonline.com/classic/ws?studioid=5747548&stype=40&prodid=102",
  },
];

const Membership: React.FC = () => {
  return (
    <div
      className="relative min-h-screen bg-scroll md:bg-fixed"
      style={{
        backgroundImage: "url('/images/membership.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Seo
        title="Passes & Memberships"
        description="Explore class passes and monthly memberships at Cycle Collective Saskatoon, including intro offers for new clients. Find the plan that fits your ride."
        path="/services"
        image="/images/membership.jpg"
      />
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/55" />

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-8 text-center text-white drop-shadow-lg">
            PASSES & MEMBERSHIPS
          </h1>

          {/* Intro Offers Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-center text-white drop-shadow-md">
              Intro Offers (New Clients Only)
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-4">
              {INTRO_OFFERS.map((offer) => (
                <OfferCard key={offer.title} {...offer} />
              ))}
            </div>
            <p className="text-sm italic text-center text-white/80 drop-shadow-sm">
              * Intro offers cannot be combined or stacked with promo codes
            </p>
          </div>

          {/* Promotions Section */}
          {/* <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-center text-white drop-shadow-md">
              Promotions
            </h2>
            <div className="grid max-w-sm mx-auto mb-4">
              {PROMOTIONS.map((offer) => (
                <OfferCard key={offer.title} {...offer} />
              ))}
            </div>
            <p className="text-sm italic text-center text-white/80 drop-shadow-sm">
              * Summer Bonus Pack expires 12 months from purchase
            </p>
          </div> */}

          {/* Class Packs Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-center text-white drop-shadow-md">
              Class Packs
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {CLASS_PACKS.map((offer) => (
                <OfferCard key={offer.title} {...offer} />
              ))}
            </div>
          </div>

          {/* Drop In Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-center text-white drop-shadow-md">
              Drop In Prices
            </h2>
            <div className="grid max-w-sm mx-auto">
              {DROP_IN.map((offer) => (
                <OfferCard key={offer.title} {...offer} />
              ))}
            </div>
          </div>

          {/* Auto Renew Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-center text-white drop-shadow-md">
              Auto Renew (Save over 20%)
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {AUTO_RENEW.map((offer) => (
                <OfferCard key={offer.title} {...offer} />
              ))}
            </div>
          </div>

          {/* Mindbody Registration Widget - script loaded in index.html */}
          <div
            className="mb-8 bg-white/90 rounded-lg p-4"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: Required for third-party Mindbody widget
            dangerouslySetInnerHTML={{
              __html: `<healcode-widget data-type="registrations" data-widget-partner="object" data-widget-id="211642969d15" data-widget-version="0"></healcode-widget>`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Membership;
